/*!
 * klaviyo-fb-lead.js
 * Production-safe Meta Lead tracking for one Klaviyo embedded form.
 *
 * What it does:
 * - Fires Meta Lead exactly once per confirmed Klaviyo success
 * - Uses Klaviyo callback first
 * - Uses DOM success state as fallback
 * - Locks on one canonical outer wrapper only
 * - Prevents duplicate Lead fires
 *
 * Install:
 * 1) Upload to Shopify assets as klaviyo-fb-lead.js
 * 2) Include before </body> in theme.liquid and password.liquid
 */

(function () {
  'use strict';

  var CONFIG = {
    DEBUG: false,
    FORM_SELECTOR: '.klaviyo-form-UqG6aT', // change only if form ID changes
    FIRE_RETRY_ATTEMPTS: 10,
    FIRE_RETRY_DELAY: 400,
    SOURCE_LABEL: 'SMS'
    // OPTIONAL only if business has a real lead value:
    // LEAD_VALUE: 5.00,
    // LEAD_CURRENCY: 'USD'
  };

  var GLOBAL_KEY = '__klaviyo_fb_lead_global_lock__';

  function log() {
    if (CONFIG.DEBUG) console.log.apply(console, arguments);
  }

  function warn() {
    if (CONFIG.DEBUG) console.warn.apply(console, arguments);
  }

  function uuidv4() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      var v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  function getWrapper() {
    return document.querySelector(CONFIG.FORM_SELECTOR);
  }

  function getGlobalState() {
    window[GLOBAL_KEY] = window[GLOBAL_KEY] || {
      callbackAttached: false,
      observerAttached: false,
      fired: false,
      pending: false,
      eventId: null
    };
    return window[GLOBAL_KEY];
  }

  function isLocked(wrapper) {
    var state = getGlobalState();

    return !!(
      state.fired ||
      state.pending ||
      (wrapper && (
        wrapper.getAttribute('data-klaviyo-lead-fired') === '1' ||
        wrapper.getAttribute('data-klaviyo-lead-pending') === '1'
      ))
    );
  }

  function setPending(wrapper, eventId) {
    var state = getGlobalState();
    state.pending = true;
    state.eventId = eventId;

    if (wrapper) {
      wrapper.setAttribute('data-klaviyo-lead-pending', '1');
      wrapper.setAttribute('data-klaviyo-lead-event-id', eventId);
    }
  }

  function clearPending(wrapper) {
    var state = getGlobalState();
    state.pending = false;

    if (wrapper) {
      wrapper.removeAttribute('data-klaviyo-lead-pending');
    }
  }

  function markFired(wrapper, eventId) {
    var state = getGlobalState();
    state.pending = false;
    state.fired = true;
    state.eventId = eventId;

    if (wrapper) {
      wrapper.setAttribute('data-klaviyo-lead-fired', '1');
      wrapper.setAttribute('data-klaviyo-lead-event-id', eventId);
      wrapper.removeAttribute('data-klaviyo-lead-pending');
    }
  }

  function buildPayload(eventId) {
    var payload = {
      source: CONFIG.SOURCE_LABEL,
      event_id: eventId,
      form_id: CONFIG.FORM_SELECTOR.replace('.klaviyo-form-', '')
    };

    // OPTIONAL:
    // only enable if client has a REAL estimated lead value
    if (
      typeof CONFIG.LEAD_VALUE === 'number' &&
      CONFIG.LEAD_CURRENCY
    ) {
      payload.value = CONFIG.LEAD_VALUE;
      payload.currency = CONFIG.LEAD_CURRENCY;
    }

    return payload;
  }

  function safeFireLead(payload, cb) {
    var tries = 0;

    function tryFire() {
      tries++;

      if (typeof fbq !== 'function') {
        if (tries < CONFIG.FIRE_RETRY_ATTEMPTS) {
          return setTimeout(tryFire, CONFIG.FIRE_RETRY_DELAY);
        }
        if (cb) cb(new Error('fbq not available'));
        return;
      }

      try {
        fbq('track', 'Lead', payload || {});
        log('[klaviyo-fb] Lead fired', payload);
        if (cb) cb(null, true);
      } catch (err) {
        if (tries < CONFIG.FIRE_RETRY_ATTEMPTS) {
          return setTimeout(tryFire, CONFIG.FIRE_RETRY_DELAY);
        }
        if (cb) cb(err);
      }
    }

    tryFire();
  }

  function fireLeadOnce(source) {
    var wrapper = getWrapper();
    if (!wrapper || isLocked(wrapper)) {
      log('[klaviyo-fb] blocked duplicate via', source);
      return;
    }

    var eventId = uuidv4();
    setPending(wrapper, eventId);

    safeFireLead(buildPayload(eventId), function (err) {
      if (err) {
        clearPending(wrapper);
        warn('[klaviyo-fb] Lead failed via', source, err);
        return;
      }

      markFired(wrapper, eventId);
      log('[klaviyo-fb] Lead confirmed via', source, eventId);
    });
  }

  function wrapperHasSuccessState() {
    var wrapper = getWrapper();
    if (!wrapper) return false;

    var successSelectors = [
      '.klaviyo-form-success',
      '.klaviyo-success',
      '.success-message',
      '[data-testid*="success"]',
      '[class*="success"]'
    ];

    for (var i = 0; i < successSelectors.length; i++) {
      if (wrapper.querySelector(successSelectors[i])) return true;
    }

    var text = (wrapper.innerText || '').toLowerCase();

    return (
      /thanks for subscribing/.test(text) ||
      /thank\s*you/.test(text) ||
      /\bthanks\b/.test(text) ||
      /\byou'?re in\b/.test(text) ||
      /\bsubscribed\b/.test(text) ||
      /\bsuccess\b/.test(text)
    );
  }

  function attachObserver() {
    var state = getGlobalState();
    if (state.observerAttached) return;

    var wrapper = getWrapper();
    if (!wrapper) return;

    state.observerAttached = true;

    var observer = new MutationObserver(function () {
      var currentWrapper = getWrapper();
      if (!currentWrapper) return;

      if (isLocked(currentWrapper)) {
        if (currentWrapper.getAttribute('data-klaviyo-lead-fired') === '1') {
          observer.disconnect();
        }
        return;
      }

      if (wrapperHasSuccessState()) {
        fireLeadOnce('dom-success');
        observer.disconnect();
      }
    });

    observer.observe(wrapper, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true
    });

    log('[klaviyo-fb] observer attached');
  }

  function attachKlaviyoCallback() {
    var state = getGlobalState();
    if (state.callbackAttached) return;

    try {
      window._klOnsite = window._klOnsite || [];
      window._klOnsite.push([
        'onFormSubmitted',
        function () {
          fireLeadOnce('klaviyo-callback');
        }
      ]);
      state.callbackAttached = true;
      log('[klaviyo-fb] callback attached');
    } catch (e) {
      warn('[klaviyo-fb] callback attach failed', e);
    }
  }

  function init() {
    attachObserver();
    attachKlaviyoCallback();

    window.__klaviyo_fb = {
      debug: function () {
        var wrapper = getWrapper();
        var state = getGlobalState();

        return {
          selector: CONFIG.FORM_SELECTOR,
          wrapperFound: !!wrapper,
          globalState: state,
          wrapperState: wrapper ? {
            fired: wrapper.getAttribute('data-klaviyo-lead-fired'),
            pending: wrapper.getAttribute('data-klaviyo-lead-pending'),
            eventId: wrapper.getAttribute('data-klaviyo-lead-event-id')
          } : null
        };
      }
    };

    log('[klaviyo-fb] init complete');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();